import React, { useEffect, useState } from 'react';
import WModal from 'shared_components/components/WModal';
import WButton from 'shared_components/components/WForms/WButton/WButton';
import { useApi } from 'shared_components/context';
import { LookupSourceArtistLookupSourceArtistResponseSourceEnum } from '../../../../../shared_components/generated/admin/apis/PortalApi';
import {
  FIELD_ERROR_CLASS,
  FIELD_HINT_CLASS,
  FIELD_LABEL_CLASS,
  MODAL_ACTIONS_CLASS,
  SegmentedChoice,
  SegmentedToggle,
  fieldControlClass,
} from './formStyles';

/**
 * Create/Add Artist modal — extracted from RecordingEditForm's artist section
 * so the same "add an artist" flow (source + source ID + Discogs auto-lookup +
 * featured status) can be reused by the bulk-edit builder.
 *
 * On confirm it hands back a fully-formed ArtistEntry via `onAdd`; the consumer
 * decides what to do with it (the edit form appends it to the recording's
 * artists; the bulk builder stores it as the row value).
 */

export type FeaturedStatus = 'fa' | 'ofa' | 'nf';

export type ArtistMember = {
  id: string;
  stageName: string;
  /** True when this member already exists in the WMC graph and `id` is the
   *  internal WMC id; false when `id` is the source-side id. */
  exists?: boolean;
};

export type ArtistEntry = {
  name: string;
  isFeatured: boolean;
  featuredStatus: FeaturedStatus;
  id?: string;
  members?: ArtistMember[];
  type?: 'persona' | 'group';
  /** When set, this artist hasn't been created yet — on save, call /create
   *  instead of including it in the /artists payload. */
  pendingCreate?: {
    source: 'discogs' | 'musicbrainz';
    sourceId: string;
  };
  /** Set when an already-linked artist was demoted rather than removed. Such an
   *  entry is a tombstone: it is hidden from the artist list (which only shows
   *  featured / other-featured artists) but kept in the value, because a plain
   *  absence would be indistinguishable from a full removal. The flag is what
   *  tells the save layer to send `op: delete` + `featuredStatus: nfa` rather
   *  than `op: delete` + `featuredStatus: null`. */
  demoted?: boolean;
};

interface ArtistEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId?: string;
  /** Recording context for the source-artist lookup. Absent when there is no
   *  recording yet (a create), where the lookup takes its recording-less
   *  route instead. */
  parentRecordingId?: string;
  /** Offer Non-Featured as a third status. Only a create can take it: an
   *  edit-add is `fa`/`ofa` by contract (4.1.3), a non-featured artist there
   *  being a demotion rather than an addition. */
  allowNonFeatured?: boolean;
  onAdd: (entry: ArtistEntry) => void;
}

const FEATURED_STATUS_OPTIONS: readonly {
  value: FeaturedStatus;
  label: string;
}[] = [
  { value: 'fa', label: 'Featured' },
  { value: 'ofa', label: 'Other Featured' },
  { value: 'nf', label: 'Non-Featured' },
];

export const ArtistEditorModal: React.FC<ArtistEditorModalProps> = ({
  isOpen,
  onClose,
  userId,
  parentRecordingId,
  allowNonFeatured = false,
  onAdd,
}) => {
  const { adminApi: api } = useApi();

  const [newArtistAlias, setNewArtistAlias] = useState('');
  const [newArtistFeatured, setNewArtistFeatured] =
    useState<FeaturedStatus>('fa');
  const [newArtistSource, setNewArtistSource] = useState<'discogs' | 'musicbrainz'>(
    'discogs'
  );
  const [newArtistSourceId, setNewArtistSourceId] = useState('');
  const [newArtistIsGroup, setNewArtistIsGroup] = useState(false);
  const [newArtistMembers, setNewArtistMembers] = useState<ArtistMember[]>([]);
  const [newArtistExists, setNewArtistExists] = useState(false);
  const [newArtistSourceLookupLoading, setNewArtistSourceLookupLoading] =
    useState(false);
  const [newArtistSourceLookupError, setNewArtistSourceLookupError] = useState<
    string | null
  >(null);

  const reset = () => {
    setNewArtistAlias('');
    setNewArtistFeatured('fa');
    setNewArtistSource('discogs');
    setNewArtistSourceId('');
    setNewArtistIsGroup(false);
    setNewArtistMembers([]);
    setNewArtistExists(false);
    setNewArtistSourceLookupError(null);
  };

  // Auto-populate stage name and members from source ID lookup (Discogs / MB).
  useEffect(() => {
    if (!isOpen || !newArtistSourceId.trim() || !userId) {
      setNewArtistSourceLookupError(null);
      return;
    }
    setNewArtistSourceLookupLoading(true);
    setNewArtistSourceLookupError(null);
    const timer = setTimeout(async () => {
      try {
        const sourceParam =
          newArtistSource === 'musicbrainz'
            ? LookupSourceArtistLookupSourceArtistResponseSourceEnum.Musicbrainz
            : LookupSourceArtistLookupSourceArtistResponseSourceEnum.Discogs;
        // The lookup is against the artist's source and reads nothing off
        // the recording, so a create — which has none — gets the same answer
        // from the sibling route. Same split as `useArtistLookup`.
        const data = parentRecordingId
          ? await api.lookupSourceArtistLookupSourceArtistResponse({
              userId,
              parentRecordingId,
              source: sourceParam,
              sourceId: newArtistSourceId.trim(),
            })
          : await api.lookupSourceArtistWithoutRecording({
              userId,
              source: sourceParam,
              sourceId: newArtistSourceId.trim(),
            });
        if (data.stageName) {
          setNewArtistAlias(data.stageName);
          setNewArtistIsGroup(data.isGroup ?? false);
          setNewArtistExists(data._exists ?? false);
          setNewArtistMembers(
            (data.members ?? []).map((m) => ({
              id: m._exists && m.internalId ? m.internalId : m.id,
              stageName: m.stageName,
              exists: m._exists ?? false,
            }))
          );
          setNewArtistSourceLookupError(null);
        } else {
          setNewArtistSourceLookupError('Artist not found for this ID');
        }
      } catch {
        setNewArtistSourceLookupError('Lookup failed — check the Source ID');
      } finally {
        setNewArtistSourceLookupLoading(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [
    newArtistSourceId,
    newArtistSource,
    isOpen,
    userId,
    parentRecordingId,
    api,
  ]);

  const closeAndReset = () => {
    reset();
    onClose();
  };

  return (
    <WModal
      isOpen={isOpen}
      onClose={closeAndReset}
      title="Create new artist"
      modalClasses="tw-max-w-lg tw-w-full tw-mx-4"
    >
      <div className="tw-mt-4 tw-space-y-4">
        {/* Source + Source ID — entered first to auto-populate stage name */}
        <div className="tw-grid tw-grid-cols-2 tw-gap-3">
          <div>
            <label
              htmlFor="create-artist-source"
              className={`${FIELD_LABEL_CLASS} tw-mb-2`}
            >
              Source
            </label>
            <select
              id="create-artist-source"
              value={newArtistSource}
              onChange={(e) => {
                setNewArtistSource(e.target.value as typeof newArtistSource);
                setNewArtistAlias('');
                setNewArtistSourceId('');
                setNewArtistSourceLookupError(null);
              }}
              className={fieldControlClass({
                kind: 'select',
                hasValue: !!newArtistSource,
              })}
            >
              <option value="discogs">Discogs</option>
              <option value="musicbrainz">MusicBrainz</option>
            </select>
          </div>
          <div>
            <label
              htmlFor="create-artist-source-id"
              className={`${FIELD_LABEL_CLASS} tw-mb-2`}
            >
              Source ID
            </label>
            <input
              id="create-artist-source-id"
              type="text"
              value={newArtistSourceId}
              onChange={(e) => {
                setNewArtistSourceId(e.target.value);
                if (!e.target.value.trim()) {
                  setNewArtistAlias('');
                  setNewArtistSourceLookupError(null);
                }
              }}
              placeholder={
                newArtistSource === 'discogs' ? 'e.g. 12345' : 'Enter source ID'
              }
              className={fieldControlClass()}
            />
          </div>
        </div>

        {/* Stage Name — auto-populated from source lookup, editable */}
        <div>
          <label
            htmlFor="create-artist-alias"
            className={`${FIELD_LABEL_CLASS} tw-mb-2`}
          >
            Stage Name
          </label>
          <div className="tw-relative">
            <input
              id="create-artist-alias"
              type="text"
              value={newArtistAlias}
              onChange={(e) => setNewArtistAlias(e.target.value)}
              placeholder={
                newArtistSourceLookupLoading
                  ? 'Looking up artist...'
                  : 'Enter stage name'
              }
              className={fieldControlClass({
                error: !!newArtistSourceLookupError || newArtistExists,
              })}
            />
            {newArtistSourceLookupLoading && (
              <span className="tw-absolute tw-right-3 tw-top-1/2 -tw-translate-y-1/2 tw-text-textSeal tw-text-xs">
                Loading...
              </span>
            )}
          </div>
          {newArtistSourceLookupError && (
            <p className={`${FIELD_ERROR_CLASS} tw-mt-1`}>
              {newArtistSourceLookupError}
            </p>
          )}
          {newArtistExists && !newArtistSourceLookupError && (
            <p className={`${FIELD_ERROR_CLASS} tw-mt-1`}>
              Artist already exists in WMC database. Use Artist Search to add
              them
            </p>
          )}
          {newArtistSource !== 'discogs' &&
            newArtistSourceId.trim() &&
            !newArtistAlias && (
              <p className={`${FIELD_HINT_CLASS} tw-mt-1`}>
                Auto-lookup is only available for Discogs. Enter the stage name
                manually.
              </p>
            )}
        </div>

        {/* Featured Status */}
        <div>
          <span className={`${FIELD_LABEL_CLASS} tw-mb-2`}>
            Featured Status
          </span>
          {allowNonFeatured ? (
            <SegmentedChoice
              options={FEATURED_STATUS_OPTIONS}
              value={newArtistFeatured}
              onChange={setNewArtistFeatured}
            />
          ) : (
            <SegmentedToggle
              leftLabel="Featured"
              rightLabel="Other Featured"
              isLeftSelected={newArtistFeatured === 'fa'}
              onSelectLeft={() => setNewArtistFeatured('fa')}
              onSelectRight={() => setNewArtistFeatured('ofa')}
            />
          )}
        </div>

        {/* Actions */}
        <div
          className={`${MODAL_ACTIONS_CLASS} tw-border-t tw-border-formInputBackground`}
        >
          <WButton variant="secondary" label="Cancel" onClick={closeAndReset} />
          <WButton
            variant="primary"
            label={newArtistSourceLookupLoading ? 'Looking up...' : 'Add'}
            disabled={
              !newArtistSourceId.trim() ||
              !newArtistAlias.trim() ||
              newArtistSourceLookupLoading ||
              newArtistExists
            }
            onClick={() => {
              if (newArtistAlias.trim() && newArtistSourceId.trim()) {
                onAdd({
                  name: newArtistAlias.trim(),
                  isFeatured: newArtistFeatured === 'fa',
                  featuredStatus: newArtistFeatured,
                  members:
                    newArtistMembers.length > 0 ? newArtistMembers : undefined,
                  pendingCreate: {
                    source: newArtistSource,
                    sourceId: newArtistSourceId.trim(),
                  },
                });
                reset();
              }
            }}
          />
        </div>
      </div>
    </WModal>
  );
};

export default ArtistEditorModal;
